import React, {useEffect, useRef, useState} from 'react'
import _ from 'lodash'
import classNames from 'classnames'
import {Formik} from 'formik'
import dayjs from 'dayjs'
import {Input, message, Select, Spin} from 'antd'
import {useHistory} from 'react-router-dom'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {
    familyBook_addOwnRelative_Api,
    familyBook_addParentBook_Api,
    familyBook_createFamilyBookMember_Api,
    familyBook_updateFamilyBookMember_Api,
    familyBookMember_resendInvite_Api,
} from 'apis/familyBook'
import {lookup_lookupInfoByValue_Api} from 'apis/member'
import {staticPaths} from 'routes/staticPaths'
import {IoAlertCircleOutline} from 'react-icons/io5'
import DrawDialog from 'components/drawer/dialog/DrawDialog'
import LookupField from 'components/LookupField'
import Translate from 'modules/local/Translate'
import {renderSaintLookupOption} from '../functions/lookupOptionRenderers'
import {ChipGroup, Field, FormActions, Section} from 'components/form/ModernForm'
import {GenderTypes} from '../../appFieldTypes'
import PersonNameField from './PersonNameField'

// Tách khỏi FamilyBookDetail.js (nơi định nghĩa gốc) sang file riêng để MemberDetailModal.js dùng
// lại được (nút sửa hồ sơ ngay trong popup Hồ Sơ Thành Viên) mà KHÔNG tạo circular import -
// FamilyBookDetail.js vốn đã `import MemberDetailModal from '../components/MemberDetailModal'`, nếu
// MemberDetailModal.js lại import ngược AddRelativeModal từ FamilyBookDetail.js sẽ tạo vòng lặp
// module (cùng lý do đã tách MomentRow.js ra khỏi FamilyBooks.js trước đây).

// Cầu nối tạm cho dữ liệu cũ đã migrate từ catholic_household_members - enum cũ chỉ có
// FATHER/MOTHER/CHILDREN (khác hẳn HEAD/SPOUSE/CHILD/PARENT/SIBLING/GRANDCHILD/OTHER mới), migrate
// trước đây copy thẳng nên vẫn còn giá trị cũ trong DB -> chỉ sửa hiển thị ở đây, CHƯA sửa dữ liệu
// gốc (cần xác nhận trước khi chạy update DB thật).
export const LEGACY_RELATIONSHIP_LABELS = {
    father: 'relationship_parent',
    mother: 'relationship_parent',
    children: 'relationship_child',
}

// family_book_member_relationship_type - khớp App\Helpers\Common::$familyBookMemberRelationshipTypes
// (quan hệ so với head_member_id của sổ). Rút gọn còn 5 lựa chọn thật cần khi THÊM MỚI (quyết định
// cùng user, xem docs/features/... nếu có): bỏ hẳn "Gia trưởng" (chỉ có đúng 1 người, gán lúc TẠO
// sổ, không thêm qua đây được) và "Anh/Chị/Em"/"Cháu" (hiếm dùng, gộp vào "Khác").
//
// "Vợ/Chồng" tách thành 2 LỰA CHỌN TĨNH "Chồng"/"Vợ" (KHÔNG phải 1 label động theo giới tính - thử
// rồi mới thấy sai: mặc định giới tính là Nam nên list chỉ hiện "Chồng", "Vợ" biến mất tới khi tự
// đổi giới tính trước, ngược đời vì phải BIẾT trước rồi mới chọn được). Y hệt cơ chế Bố/Mẹ: đây là 2
// sentinel value 'SPOUSE_HUSBAND'/'SPOUSE_WIFE' chỉ tồn tại trong form, bấm vào tự set luôn giới
// tính tương ứng (ẩn Section Giới tính đi, khỏi chọn trùng lặp) - lưu xuống mới quy về đúng 1
// relationship_type='SPOUSE' thật (xem onSubmit).
//
// "Cha/Mẹ" cũ tách thành 2 lựa chọn "Bố"/"Mẹ" ĐẶC BIỆT: KHÔNG lưu thẳng vào relationship_type như
// các lựa chọn khác (BE không có enum FATHER/MOTHER) - đây là 2 sentinel value chỉ dùng nội bộ form
// để kích hoạt luồng addParentBook() (tự tạo/gộp 1 sổ RIÊNG cho Bố/Mẹ + link + gán chung
// family_person_id, xem FamilyBookController::addParentBook()) thay vì luồng tạo member bình
// thường - đúng mô hình 1 sổ = 1 cặp vợ chồng + con cái, cha mẹ của gia trưởng phải có sổ riêng.
export const SPOUSE_SENTINELS = ['SPOUSE_HUSBAND', 'SPOUSE_WIFE']
export const PARENT_SPLIT_ROLES = ['FATHER', 'MOTHER']
// Giới tính ẩn/tự set cho các lựa chọn đã tự mang giới tính rõ ràng - khỏi bắt chọn lại thừa.
const GENDER_BY_SENTINEL = {
    SPOUSE_HUSBAND: GenderTypes.Male.value,
    SPOUSE_WIFE: GenderTypes.Female.value,
    FATHER: GenderTypes.Male.value,
    MOTHER: GenderTypes.Female.value,
}

// relationship_type THẬT lưu ở DB ('SPOUSE') + giới tính -> sentinel form dùng để prefill Select khi
// SỬA 1 bản ghi đã có sẵn (ngược lại với bước map-về-thật lúc submit).
export const mapStoredToFormRelationship = (relationship_type, gender) => {
    if (relationship_type === 'SPOUSE') {
        return gender === GenderTypes.Female.value ? 'SPOUSE_WIFE' : 'SPOUSE_HUSBAND'
    }
    return relationship_type
}

// 2 group riêng biệt trong Select (antd options dạng group: [{label, options:[...]}]) - Bố/Mẹ tách
// hẳn khỏi nhóm quan hệ "trong sổ này" vì bấm vào KHÔNG chỉ thêm 1 thành viên bình thường mà còn
// tạo/liên kết 1 SỔ MỚI phía sau - cần nhìn phát biết ngay đây là hành động khác hẳn, không lẫn vào
// các lựa chọn còn lại.
// `book` - CHỈ cần để check is_org_managed (sổ do giáo xứ trực tiếp quản lý thì ẩn hẳn nhóm Bố/Mẹ,
// org không được tự tách sổ mới thay giáo dân - xem is_org_managed ở FamilyBookTransformer + guard
// tương ứng ở FamilyBookController::addParentBook()). Không truyền `book` (vd gọi từ
// MemberDetailModal.js, nhánh Bố/Mẹ vốn đã ẩn theo editingMember) -> coi như không org-managed, giữ
// nguyên hành vi cũ.
const buildRelationshipOptions = (editingMember, t, book) => {
    const withinBookOptions = [
        {value: 'SPOUSE_HUSBAND', label: t('relationship_husband')},
        {value: 'SPOUSE_WIFE', label: t('relationship_wife')},
        {value: 'CHILD', label: t('relationship_child')},
        {value: 'OTHER', label: t('relationship_other')},
    ]
    // Dữ liệu cũ (Gia trưởng/Cha Mẹ/Anh Chị Em/Cháu) - không còn cho CHỌN MỚI nhưng vẫn phải hiện
    // đúng khi sửa 1 bản ghi cũ đã có sẵn giá trị đó, không ép user phải đổi giá trị mới hiểu được.
    const mappedValue = editingMember && mapStoredToFormRelationship(editingMember.relationship_type, editingMember.gender)
    if (mappedValue && !withinBookOptions.some((o) => o.value === mappedValue)) {
        withinBookOptions.push({value: mappedValue, label: t(`relationship_${mappedValue.toLowerCase()}`)})
    }

    const groups = [{label: t('relationship group within book'), options: withinBookOptions}]
    // Bố/Mẹ chỉ hiện khi THÊM MỚI (không thể "tách sổ" cho 1 bản ghi đã tồn tại sẵn) VÀ sổ không
    // phải do giáo xứ trực tiếp quản lý (xem comment is_org_managed ở khai báo hàm).
    if (!editingMember && !book?.is_org_managed) {
        groups.push({
            label: t('relationship group create new book'),
            options: [
                {value: 'FATHER', label: t('relationship_father')},
                {value: 'MOTHER', label: t('relationship_mother')},
            ],
        })
    }
    return groups
}

const GENDER_OPTIONS = Object.values(GenderTypes).map((g) => ({label: g.label, value: g.value}))

// editingMember có giá trị -> chế độ SỬA (PUT vào member đã có, prefill giá trị hiện tại), không
// truyền -> chế độ THÊM MỚI như cũ. Dùng chung 1 form thay vì tách 2 bản gần như giống hệt nhau.
// `book` (item đầy đủ từ familyBook_getById_Api, có head_member/head_spouse_member) - CHỈ cần cho
// nhánh Bố/Mẹ (chọn "của ai" khi sổ đã có đủ cặp vợ chồng) - KHÔNG BAO GIỜ dùng khi editingMember có
// giá trị (nhánh Bố/Mẹ ẩn hẳn lúc sửa), nên gọi từ MemberDetailModal.js có thể bỏ trống prop này.
// `anchorMemberId`/`anchorMemberName`/`defaultRelationshipType` - "+" nhanh Chồng/Vợ/Con từ popup
// Hồ Sơ Thành Viên (RelativesCard) - KHÁC hẳn `familyBookId` (đã biết sẵn sổ cần thêm vào):
// anchorMemberId là NGƯỜI ĐANG XEM, có thể CHƯA có sổ riêng - BE tự tạo/dùng lại qua
// familyBook_addOwnRelative_Api (xem FamilyBookMember::ensureOwnFamilyBook()).
// `lockRelationship` - ẩn Section "Quan hệ", quan hệ CỐ ĐỊNH theo đúng nút đã bấm (không cho chọn
// lại quan hệ khác) - LUÔN true khi có anchorMemberId (auto-tạo sổ chỉ hợp lý cho ĐÚNG 1 quan hệ đã
// định), nhưng cũng dùng độc lập cho nút "+ Anh/Chị/Em" (RelativesCard) - quan hệ đó vẫn thêm vào
// familyBookId có sẵn (không cần anchorMemberId/tạo sổ) nhưng KHÔNG được cho chọn Bố/Mẹ ở đây (sổ
// đã có Cha/Mẹ, chọn lại sẽ tạo nhầm 1 sổ tách khác - xem RelativesCard).
const AddRelativeForm = ({familyBookId, book, editingMember, anchorMemberId, anchorMemberName, defaultRelationshipType, lockRelationship, forcedForMember, onClose, onSuccess, t}) => {
    const history = useHistory()
    const {handleAsyncAction: doCreate, loading: creating} = useAsyncAction({
        apiInfo: familyBook_createFamilyBookMember_Api,
        onSuccess: () => {
            message.success(t('member added'))
            onSuccess && onSuccess()
        },
        onError: () => message.error(t('error occurred')),
    })
    const {handleAsyncAction: doUpdate, loading: updating} = useAsyncAction({
        apiInfo: familyBook_updateFamilyBookMember_Api,
        onSuccess: () => {
            message.success(t('member updated'))
            onSuccess && onSuccess()
        },
        onError: () => message.error(t('error occurred')),
    })
    // Bố/Mẹ - tự tạo/gộp sổ riêng + link + gán chung family_person_id (xem
    // FamilyBookController::addParentBook()). Thành công -> báo rõ đã phát sinh sổ mới rồi chuyển
    // thẳng về tab "Sổ Gia Đình" để user THẤY sổ mới đó luôn, không chỉ tin theo lời báo suông.
    const {handleAsyncAction: doAddParent, loading: addingParent} = useAsyncAction({
        apiInfo: familyBook_addParentBook_Api,
        onSuccess: (result, {response}) => {
            const isNewBook = _.get(response, 'data.is_new_book')
            message.success(t(isNewBook ? 'parent book created success' : 'parent added to existing book success'))
            onClose && onClose()
            history.push(staticPaths.familyBooks.pathname)
        },
        onError: () => message.error(t('error occurred')),
    })
    // Chồng/Vợ/Con nhanh - KHÁC Bố/Mẹ ở trên: KHÔNG điều hướng đi đâu cả, đóng modal + refresh
    // đúng ngay tại popup Hồ Sơ Thành Viên đang xem (onSuccess do MemberDetailModal.js truyền vào,
    // xem setRefreshToken) - hành động "nhanh" nên giữ user tại chỗ để thấy kết quả liền, không kéo
    // sang tab Sổ Gia Đình như luồng Bố/Mẹ (đó là chuyển hẳn ngữ cảnh sang 1 sổ khác/mới).
    const {handleAsyncAction: doAddOwnRelative, loading: addingOwnRelative} = useAsyncAction({
        apiInfo: familyBook_addOwnRelative_Api,
        onSuccess: (result, {response}) => {
            const isNewBook = _.get(response, 'data.is_new_book')
            message.success(t(isNewBook ? 'relative added new book created' : 'relative added'))
            onClose && onClose()
            onSuccess && onSuccess()
        },
        onError: () => message.error(t('error occurred')),
    })
    // "Gửi lại thư mời" - CHỈ hoạt động ở chế độ SỬA (editingMember có id thật), member CHƯA liên kết
    // tài khoản. Response trả về CHÍNH member (familyBookMemberSchema) -> Redux tự cập nhật
    // invite_sent_count/invite_last_sent_at/can_resend_invite mới nhất lên editingMember qua
    // normalize, không cần tự set state riêng (đúng convention "data qua Redux").
    const {handleAsyncAction: doResendInvite, loading: resendingInvite} = useAsyncAction({
        apiInfo: familyBookMember_resendInvite_Api,
        onSuccess: () => message.success(t('invite email sent')),
        onError: (err) => message.error(_.get(err, 'response.data.message') || t('error occurred')),
    })
    const loading = creating || updating || addingParent || addingOwnRelative

    const initialValues = editingMember ? {
        relationship_type: mapStoredToFormRelationship(editingMember.relationship_type, editingMember.gender) || 'CHILD',
        gender: editingMember.gender || GenderTypes.Male.value,
        patron_saint: editingMember.patron_saint || null,
        patron_saint_name: editingMember.patron_saint_name || '',
        patron_saint_id: editingMember.patron_saint?.id || null,
        member_user: editingMember.member_user || null,
        member_user_id: editingMember.member_user?.id || null,
        name: editingMember.name || '',
        // birth_date/death_date: lưu string 'YYYY-MM-DD' thẳng (không bọc dayjs) - đúng kiểu native
        // <input type="date"> cần, xem lý do đổi từ antd DatePicker sang native ở Section bên dưới.
        birth_date: editingMember.birth_date ? dayjs(editingMember.birth_date).format('YYYY-MM-DD') : '',
        death_date: editingMember.death_date ? dayjs(editingMember.death_date).format('YYYY-MM-DD') : '',
        // contact_email/contact_phone: BE đã tự resolve ưu tiên tài khoản liên kết (xem
        // FamilyBookMemberTransformer) - editingMember.contact_email/phone LUÔN đúng giá trị cần hiện
        // (dù đang liên kết hay không), không cần tự tính lại ở FE.
        contact_email: editingMember.contact_email || '',
        contact_phone: editingMember.contact_phone || '',
    } : {
        relationship_type: defaultRelationshipType || 'CHILD',
        gender: GENDER_BY_SENTINEL[defaultRelationshipType] || GenderTypes.Male.value,
        patron_saint: null,
        patron_saint_name: '',
        patron_saint_id: null,
        member_user: null,
        member_user_id: null,
        name: '',
        birth_date: '',
        death_date: '',
        contact_email: '',
        contact_phone: '',
    }

    // Ngày mất mặc định ẨN, chỉ hiện khi người dùng chủ động bấm mở (hoặc đã có sẵn dữ liệu khi
    // sửa) - đây là thông tin tế nhị/không may mắn theo quan niệm, không nên phơi ra ngay trong
    // luồng nhập liệu chính cho MỌI thân nhân (đa số vẫn đang còn sống).
    const [showDeathDate, setShowDeathDate] = useState(!!initialValues.death_date)

    // genderTouched - KHÁC birth_date/patron_saint (2 field đó tự có trạng thái "trống" rõ ràng để
    // biết còn tự do ghi đè theo tài khoản lookup hay không, xem onLink bên dưới): gender LUÔN có
    // sẵn 1 giá trị mặc định (Nam) dù chưa ai chọn gì, không thể dùng "rỗng hay không" để phân biệt
    // "user đã chủ động chọn" với "còn nguyên mặc định". Theo dõi riêng bằng cờ này - true ngay từ
    // đầu khi SỬA (giá trị ban đầu lấy từ DB, đã là lựa chọn thật của người dùng trước đó, không phải
    // placeholder) hoặc sau khi user bấm ChipGroup ít nhất 1 lần - dùng để quyết định lookup tài
    // khoản có được ghi đè gender hay không (không ghi đè nếu đã "touched").
    const [genderTouched, setGenderTouched] = useState(!!editingMember)

    // Bố/Mẹ đang khai là CỦA AI - gia trưởng hay vợ/chồng gia trưởng (2 sổ HOÀN TOÀN KHÁC NHAU, vd
    // bố/mẹ chồng vs bố/mẹ vợ - xem FamilyBookController::addParentBook()). Chỉ cần hỏi khi sổ đã có
    // ĐỦ cặp vợ chồng (head_member + head_spouse_member) - sổ mới chỉ có 1 mình gia trưởng thì không
    // có lựa chọn nào khác ngoài HEAD, hỏi thêm chỉ gây rối.
    // `forcedForMember` - "+" nhanh Cha/Mẹ từ popup Hồ Sơ Thành Viên (RelativesCard) đã BIẾT CHẮC
    // đang thêm cha/mẹ của AI (chính người đang xem, qua item.relationship_type) - bỏ qua hẳn bước
    // hỏi "của ai" (không cần book.head_spouse_member nữa, RelativesCard chỉ truyền book Tiny sẵn
    // có, không có field đó).
    const [forMember, setForMember] = useState('HEAD')
    const canChooseForMember = !forcedForMember && !!(book?.head_member && book?.head_spouse_member)

    // Dò khớp tài khoản Kristo THEO EMAIL đang gõ ở "Liên hệ" (khác PersonNameField - tra cứu CHỦ
    // ĐỘNG qua nút tìm): tự kiểm tra ngầm khi gõ đủ định dạng email, khớp thì LINK LUÔN (gọi
    // applyMatchedAccountRef.current, trỏ tới applyMatchedAccount() bên trong Formik render-prop -
    // CÙNG 1 hàm với chọn thủ công qua PersonNameField, không tách 2 nguồn set member_user_id) để
    // hiện NGAY ai sẽ được gắn vào thay vì để user bất ngờ sau khi lưu (BE tự làm y hệt lúc lưu,
    // xem FamilyBookMemberController::normalizeMemberLookups()) - gõ nhầm/không đúng người thì gỡ
    // lại được qua nút X ở badge dưới Họ và tên (cùng UI với liên kết qua tên).
    //
    // Tái dùng NGUYÊN lookup_lookupInfoByValue_Api (search_type=user/input_type=email) đang phục vụ
    // UserLookupComponent, không tạo API riêng cho cùng 1 việc tra cứu. Debounce 700ms - cùng hằng
    // số LOOKUP_DEBOUNCE UserLookupComponent đang dùng cho hành vi "gõ xong mới tra cứu", không tự
    // chế số khác cho cùng 1 hành vi.
    const EMAIL_MATCH_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/
    const emailMatchDebounceRef = useRef(null)
    const emailMatchCheckedRef = useRef(null) // email cuối cùng đã tra - tránh tra lại y hệt
    const applyMatchedAccountRef = useRef(null) // gán lại mỗi render trong Formik render-prop bên dưới
    const [checkingEmailMatch, setCheckingEmailMatch] = useState(false)

    const {handleAsyncAction: checkEmailMatch} = useAsyncAction({
        apiInfo: lookup_lookupInfoByValue_Api,
        onSuccess: (result, {response}) => {
            setCheckingEmailMatch(false)
            const matched = _.get(response, 'data', {})
            if (matched?.name && applyMatchedAccountRef.current) applyMatchedAccountRef.current(matched)
        },
        onError: () => setCheckingEmailMatch(false),
    })

    useEffect(() => {
        return () => {
            if (emailMatchDebounceRef.current) clearTimeout(emailMatchDebounceRef.current)
        }
    }, [])

    const triggerEmailMatch = (email) => {
        if (emailMatchDebounceRef.current) clearTimeout(emailMatchDebounceRef.current)
        const trimmed = (email || '').trim()
        if (!trimmed || !EMAIL_MATCH_REGEX.test(trimmed) || trimmed === emailMatchCheckedRef.current) {
            setCheckingEmailMatch(false)
            return
        }
        setCheckingEmailMatch(true)
        emailMatchDebounceRef.current = setTimeout(() => {
            emailMatchCheckedRef.current = trimmed
            checkEmailMatch({}, {search_type: 'user', input_type: 'email', input_value: trimmed})
        }, 700)
    }

    const validate = (values) => {
        const errors = {}
        if (!values.name) errors.name = t('required field')
        return errors
    }

    return (
        <Formik
            initialValues={initialValues}
            validate={validate}
            validateOnMount
            onSubmit={(values) => {
                // Bố/Mẹ - sentinel value CHỈ tồn tại trong form, không phải relationship_type thật
                // (xem PARENT_SPLIT_ROLES) - rẽ hẳn sang luồng addParentBook(), không đụng
                // doCreate/doUpdate bình thường.
                if (PARENT_SPLIT_ROLES.includes(values.relationship_type)) {
                    doAddParent({
                        parent_role: values.relationship_type,
                        // for_member: forcedForMember (đã biết chắc, xem khai báo canChooseForMember)
                        // ưu tiên tuyệt đối; không thì chỉ có ý nghĩa (và chỉ hỏi user) khi sổ đã đủ
                        // cặp vợ chồng - sổ chỉ mới có gia trưởng thì luôn là HEAD (mặc định
                        // canChooseForMember=false giữ nguyên forMember='HEAD', khớp default cùng
                        // phía BE khi thiếu tham số).
                        for_member: forcedForMember || (canChooseForMember ? forMember : 'HEAD'),
                        patron_saint_name: values.patron_saint_name || undefined,
                        patron_saint_id: values.patron_saint_id || undefined,
                        name: values.name,
                        member_user_id: values.member_user_id || undefined,
                        birth_date: values.birth_date || undefined,
                    }, {id: familyBookId})
                    return
                }

                const payload = {
                    // SPOUSE_HUSBAND/SPOUSE_WIFE - sentinel chỉ tồn tại trong form (xem
                    // SPOUSE_SENTINELS), quy về đúng 1 relationship_type='SPOUSE' thật khi lưu.
                    relationship_type: SPOUSE_SENTINELS.includes(values.relationship_type) ? 'SPOUSE' : values.relationship_type,
                    gender: values.gender,
                    patron_saint_name: values.patron_saint_name || undefined,
                    patron_saint_id: values.patron_saint_id || undefined,
                    name: values.name,
                    // member_user_id: có -> BE tự link chéo sổ (canh trùng qua family_person_id dựa
                    // theo cùng 1 user thật) - đúng như dữ liệu cũ đã tự link được. Chỉ có khi chọn
                    // đúng 1 user thật trong LookupField (gõ tên tự do thì để trống, không "chế" ID).
                    member_user_id: values.member_user_id || undefined,
                    // Khi SỬA: nếu trước đó đã có ngày sinh/ngày mất mà giờ bị xoá trắng (bấm clear
                    // trên native date input) phải gửi null tường minh để BE thật sự xoá - gửi
                    // undefined thì key bị bỏ khỏi payload, BE (chỉ update field có trong request) sẽ
                    // giữ nguyên giá trị cũ, tưởng đã xoá nhưng thực ra không đổi gì.
                    birth_date: values.birth_date || (editingMember?.birth_date ? null : undefined),
                    death_date: values.death_date || (editingMember?.death_date ? null : undefined),
                    // contact_email/contact_phone: CHỈ gửi khi CHƯA liên kết tài khoản - đã liên kết
                    // (member_user_id) thì 2 field này bị bỏ qua lúc đọc (BE luôn ưu tiên email/SĐT
                    // tài khoản, xem FamilyBookMemberTransformer), gửi lên cũng vô nghĩa và dễ để lại
                    // dữ liệu "ma" hiện lại nếu sau này gỡ liên kết - không gửi để DB luôn phản ánh
                    // đúng những gì user THỰC SỰ tự khai khi không có tài khoản.
                    contact_email: values.member_user_id ? undefined : (values.contact_email || (editingMember?.contact_email ? null : undefined)),
                    contact_phone: values.member_user_id ? undefined : (values.contact_phone || (editingMember?.contact_phone ? null : undefined)),
                }
                if (editingMember) {
                    doUpdate(payload, {id: editingMember.id})
                } else if (anchorMemberId) {
                    doAddOwnRelative(payload, {id: anchorMemberId})
                } else {
                    doCreate(payload, {id: familyBookId})
                }
            }}>
            {({values, touched, errors, setFieldValue, setValues, handleSubmit, isSubmitting, isValid}) => {
                const isParentSplit = PARENT_SPLIT_ROLES.includes(values.relationship_type)
                // Vợ/Chồng/Bố/Mẹ đều đã tự mang giới tính rõ ràng qua chính lựa chọn - ẩn hẳn Section
                // Giới tính, khỏi bắt chọn trùng lặp/mâu thuẫn với lựa chọn quan hệ.
                const genderImplied = !!GENDER_BY_SENTINEL[values.relationship_type]

                // Áp 1 tài khoản Kristo đã tìm/khớp được vào form - DÙNG CHUNG cho 2 nguồn: (1) chọn
                // thủ công qua nút tìm ở PersonNameField (auto=false), (2) tự dò khớp theo email đang
                // gõ ở Section "Liên hệ" bên dưới (auto=true, xem triggerEmailMatch/applyMatchedAccountRef
                // khai báo ở đầu component) - 1 NGUỒN DUY NHẤT quyết định field nào giữ nguyên/field
                // nào lấy theo tài khoản, không lặp lại logic diff field ở 2 nơi.
                // auto=true CÓ THÊM guard "chỉ áp khi CHƯA liên kết": kết quả tra cứu email về SAU khi
                // debounce (700ms + round-trip) nên `values` ở đây LUÔN là bản mới nhất tại thời điểm
                // áp (closure được gán lại applyMatchedAccountRef.current mỗi render, xem dưới) - nếu
                // trong lúc chờ đó user đã tự chọn 1 tài khoản khác qua tên (member_user_id đã có) thì
                // KHÔNG được đè lên lựa chọn thủ công đó.
                const applyMatchedAccount = (userInfo, {auto = false} = {}) => {
                    if (auto && values.member_user_id) return
                    const {id, gender, date_of_birth, patron_saint, patron_saint_name, email, phone_number} = userInfo ?? {}
                    setValues({
                        ...values,
                        member_user: userInfo?.owner || null,
                        member_user_id: id || null,
                        // gender/birth_date/patron_saint: CÙNG NGUYÊN TẮC với tên (xem PersonNameField) -
                        // dữ liệu ĐÃ có sẵn (user đã chọn/đã gõ, hoặc đang sửa 1 bản ghi đã có sẵn giá
                        // trị thật từ DB) LUÔN thắng, tài khoản lookup CHỈ điền vào chỗ còn trống/chưa ai
                        // đụng tới - tránh lặp lại đúng bug đã sửa ở tên (chọn tài khoản xong âm thầm ghi
                        // đè dữ liệu gia phả đã nhập, phải gỡ liên kết mới sửa lại được).
                        // Vợ/Chồng/Bố/Mẹ (genderImplied) đã ép giới tính rõ ràng theo lựa chọn quan hệ -
                        // luôn giữ nguyên, không cho tài khoản ghi đè.
                        gender: (genderImplied || genderTouched) ? values.gender : (_.find(GenderTypes, {value: gender})?.value ?? values.gender),
                        // date_of_birth từ API đã là string 'YYYY-MM-DD' sẵn (Common::formatCarbonDateOnly)
                        // - đúng kiểu birth_date cần cho native <input type="date">, không bọc dayjs().
                        birth_date: values.birth_date || date_of_birth,
                        patron_saint: values.patron_saint || patron_saint,
                        patron_saint_name: values.patron_saint_name || patron_saint_name,
                        patron_saint_id: values.patron_saint_id || patron_saint?.id,
                        // Liên hệ: preview NGAY email/SĐT của tài khoản vừa chọn (2 ô sẽ bị disable ngay
                        // bên dưới vì member_user_id đã có) - không phải để LƯU (payload lúc submit bỏ
                        // qua khi đã liên kết, xem onSubmit), chỉ để user thấy đúng số/email sẽ dùng mà
                        // không cần lưu-rồi-mở-lại mới thấy. Liên hệ KHÔNG áp dụng nguyên tắc "giữ cái đã
                        // có" như trên - 2 field này vốn CHỈ hiển thị (không nhập được) ngay khi đã liên
                        // kết (xem Section "contact info" bên dưới), nên preview luôn phải khớp đúng tài
                        // khoản vừa chọn, không giữ giá trị cũ (có thể là của lần gõ tay trước lúc chưa
                        // liên kết, hoặc của 1 tài khoản đã gỡ trước đó).
                        contact_email: email || values.contact_email,
                        contact_phone: phone_number || values.contact_phone,
                    })
                }
                // Luôn trỏ tới bản MỚI NHẤT của applyMatchedAccount (đóng theo values/setValues của
                // đúng render này) - checkEmailMatch (khai báo ở outer scope component, không truy cập
                // được values/setValues của Formik render-prop) gọi qua ref này khi kết quả tra cứu
                // email trả về, xem khai báo applyMatchedAccountRef ở đầu component.
                applyMatchedAccountRef.current = (userInfo) => applyMatchedAccount(userInfo, {auto: true})

                return (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div className="font-bold text-lg text-color-000 uppercase tracking-wide text-center">
                            {editingMember ? t('edit relative') : t('add relative')}
                        </div>

                        {/* lockRelationship - quan hệ đã CỐ ĐỊNH theo đúng nút "+" vừa bấm (Chồng/
                            Vợ/Con/Anh-Chị-Em), không cho chọn lại - ẩn hẳn Select, khỏi cho phép chọn
                            ra 1 quan hệ khác với nút đã bấm (vd bấm "+ Con" mà lại chọn "Vợ", hoặc
                            bấm "+ Anh/Chị/Em" mà lại chọn "Bố/Mẹ" - sổ đã có Cha/Mẹ, chọn lại sẽ tạo
                            nhầm 1 sổ tách khác). */}
                        {!lockRelationship && (
                            <Section title={t('relationship')} subtitle={t('relative to head of household')} theme="highlight">
                                <Select
                                    size="large"
                                    className="w-full"
                                    value={values.relationship_type}
                                    onChange={(v) => {
                                        setFieldValue('relationship_type', v)
                                        // Vợ/Chồng/Bố/Mẹ tự mang giới tính rõ ràng - set luôn, khỏi bắt
                                        // chọn thêm ở Section Giới tính bên dưới (đang bị ẩn cho các lựa
                                        // chọn này, xem genderImplied).
                                        if (GENDER_BY_SENTINEL[v]) setFieldValue('gender', GENDER_BY_SENTINEL[v])
                                    }}
                                    options={buildRelationshipOptions(editingMember, t, book)}
                                />
                            </Section>
                        )}

                        {/* Bố/Mẹ - báo TRƯỚC rõ ràng những gì sắp xảy ra (tạo/gộp 1 sổ riêng, liên
                            kết 2 sổ) trước khi user bấm Lưu - đúng yêu cầu "có thông báo trước cái sẽ
                            diễn ra", không để họ bấm xong mới ngỡ ngàng vì phát sinh cả 1 sổ mới. */}
                        {isParentSplit && (
                            <div className="p-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                                <IoAlertCircleOutline size={18} className="flex-shrink-0 mt-0.5 text-amber-600"/>
                                <span className="text-xs text-amber-800 leading-snug">
                                    {t(values.relationship_type === 'FATHER' ? 'parent split hint father' : 'parent split hint mother')}
                                </span>
                            </div>
                        )}

                        {/* Chồng/Vợ/Con nhanh - CHƯA biết trước lúc mở form là sẽ tạo sổ mới hay
                            dùng sổ có sẵn (phụ thuộc trạng thái thật ở BE lúc bấm Lưu, xem
                            ensureOwnFamilyBook()) nên báo TRƯỚC dạng khả năng thay vì khẳng định chắc
                            - vẫn đúng tinh thần "báo trước cái có thể xảy ra" như nhánh Bố/Mẹ. */}
                        {!!anchorMemberId && (
                            <div className="p-3 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl">
                                <IoAlertCircleOutline size={18} className="flex-shrink-0 mt-0.5 text-amber-600"/>
                                <span className="text-xs text-amber-800 leading-snug">
                                    {t('own relative hint', {name: anchorMemberName || ''})}
                                </span>
                            </div>
                        )}

                        {/* Bố/Mẹ của gia trưởng và Bố/Mẹ của vợ/chồng gia trưởng là 2 SỔ HOÀN TOÀN
                            KHÁC NHAU (vd bố/mẹ chồng vs bố/mẹ vợ) - chỉ hỏi khi sổ đã có ĐỦ cặp vợ
                            chồng (canChooseForMember), tránh hỏi thừa lúc sổ mới chỉ có 1 mình gia
                            trưởng (chỉ có 1 lựa chọn hợp lệ, không cần chọn). */}
                        {isParentSplit && canChooseForMember && (
                            <Section title={t('parent split for whom')}>
                                {/* KHÔNG dùng ChipGroup - label ở đây ghép tên thật (động), đưa qua
                                    t() như ChipGroup tự làm sẽ bị hiểu nhầm là 1 key dịch (lệch hẳn
                                    với string gốc, vỡ ở debug mode - xem modules/local/index.js). Tự
                                    dựng 2 nút chip cùng style thay vì tái dùng component. */}
                                <div className="flex flex-wrap gap-2">
                                    {[
                                        {value: 'HEAD', member: book.head_member},
                                        {value: 'SPOUSE', member: book.head_spouse_member},
                                    ].map(({value, member}) => (
                                        <button
                                            key={value}
                                            type="button"
                                            onClick={() => setForMember(value)}
                                            className={classNames(
                                                'px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 cursor-pointer',
                                                forMember === value
                                                    ? 'bg-family-700 border-family-700 text-white'
                                                    : 'bg-white border-color-100 text-color-300 hover:border-family-300 hover:text-family-700'
                                            )}>
                                            {t(values.relationship_type === 'FATHER' ? 'relationship_father' : 'relationship_mother')} {member.name}
                                        </button>
                                    ))}
                                </div>
                            </Section>
                        )}

                        {!genderImplied && (
                            <Section title={t('gender')}>
                                {/* required - giới tính bắt buộc luôn có giá trị, không cho bấm lại
                                    chip mặc định (Nam) để tự "bỏ chọn" về null (đã gặp thực tế). */}
                                <ChipGroup
                                    required
                                    options={GENDER_OPTIONS}
                                    value={values.gender}
                                    onChange={(v) => {
                                        setFieldValue('gender', v)
                                        setGenderTouched(true)
                                    }}
                                />
                            </Section>
                        )}

                        <Section title={t('holy name & full name')}>
                            {/* Xếp 2 dòng riêng (không grid-cols-2) - "Họ và tên" dài hơn hẳn "Tên
                                thánh" vì có thêm nút tra cứu + dòng chú thích, ép chung 1 hàng 2 cột
                                hẹp làm vỡ layout trên mobile. */}
                            <div className="flex flex-col gap-3">
                                <Field label={t('patron saint name')}>
                                    <Translate>
                                        {(tt) => (
                                            <LookupField
                                                title={tt('choose patron saint')}
                                                createIfNotExists={true}
                                                defaultValue={values.patron_saint_name}
                                                selectedOption={values.patron_saint}
                                                apiPath="/lookup/saint/get-by-types"
                                                name="patron_saint"
                                                renderOption={renderSaintLookupOption}
                                                handleChange={(e) => setValues({
                                                    ...values,
                                                    patron_saint: e.target,
                                                    patron_saint_name: e.target.common_name || e.target.value,
                                                    patron_saint_id: e.target.id,
                                                })}
                                            />
                                        )}
                                    </Translate>
                                </Field>
                                {/* Tên - LUÔN là input tự do sửa được (kể cả đã liên kết tài khoản),
                                    tách bạch khỏi việc liên kết - xem giải thích đầy đủ ở
                                    PersonNameField.js (bug cũ: chọn xong tài khoản là khoá cứng ô tên
                                    theo tên tài khoản, tên trang trọng trong gia phả bị "đè" mất). Nút
                                    tra cứu riêng mở UserLookupComponent - chỉ tìm khớp CHÍNH XÁC theo
                                    email hoặc mã định danh (CCCD...), không fuzzy-match theo tên (an
                                    toàn dữ liệu cá nhân, tương tự "type + validate" kiểu Google). */}
                                <Field label={t('full name')} required error={touched.name && errors.name}>
                                    <PersonNameField
                                        name={values.name}
                                        onNameChange={(v) => setFieldValue('name', v)}
                                        linkedUser={values.member_user}
                                        onLink={(userInfo) => applyMatchedAccount(userInfo)}
                                        onUnlink={() => setValues({...values, member_user: null, member_user_id: null})}
                                        placeholder={t('full name placeholder')}
                                        t={t}
                                    />
                                </Field>
                            </div>
                        </Section>

                        {/* Liên hệ - ĐÃ liên kết tài khoản (member_user_id) thì email/SĐT LUÔN lấy từ
                            chính tài khoản đó (values.contact_email/phone đã được set sẵn = preview
                            của tài khoản, xem UserLookupComponent.handleApply ở trên) - disable 2 ô,
                            không cho gõ đè (payload lúc submit cũng bỏ qua khi đã liên kết, xem
                            onSubmit) để khỏi lệch giữa cái hiện trên UI và cái thật sự dùng. CHƯA liên
                            kết -> Input tự do bình thường, cùng convention contact_email/contact_phone
                            đã dùng ở cấp SỔ (EditFamilyBookModal.js). */}
                        <Section title={t('contact info')} subtitle={values.member_user_id ? t('contact info from linked account') : t('optional')}>
                            <div className="flex flex-col gap-3">
                                <Field label={t('contact email')}>
                                    <Input
                                        size="large"
                                        type="email"
                                        disabled={!!values.member_user_id}
                                        suffix={checkingEmailMatch ? <Spin size="small"/> : null}
                                        value={values.contact_email ?? values.member_user?.email}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            setFieldValue('contact_email', val)
                                            // Dò khớp NGẦM theo email đang gõ - khớp thì applyMatchedAccountRef
                                            // (gán trong Formik render-prop ở trên) tự link luôn, xem giải
                                            // thích đầy đủ ở khai báo triggerEmailMatch/checkEmailMatch đầu
                                            // component. Chỉ có thể bắn ở đây vì field disabled ngay khi đã
                                            // liên kết (không còn onChange nào nữa sau khi link).
                                            triggerEmailMatch(val)
                                        }}
                                        placeholder={t('contact email placeholder')}
                                    />
                                    {/* CHƯA liên kết - nhắc trước hệ quả (khớp email trùng 1 tài khoản Kristo
                                        có sẵn -> TỰ ĐỘNG liên kết ngay khi gõ xong, xem triggerEmailMatch),
                                        khác hẳn cảnh báo "chưa có tài khoản -> mời" bên dưới (chỉ hiện sau khi
                                        đã lưu, chế độ sửa). Ẩn khi đã liên kết (field đang disabled). Cùng
                                        convention hint tĩnh dưới field đã dùng cho auto-link theo giấy tờ tùy
                                        thân, xem StudentForm.js ("id lookup guidance"). */}
                                    {!values.member_user_id && (
                                        <span className="text-2xs text-color-300 italic tracking-wider">
                                            {t('contact email autolink guidance')}
                                        </span>
                                    )}
                                </Field>
                                <Field label={t('contact phone')}>
                                    <Input
                                        size="large"
                                        disabled={!!values.member_user_id}
                                        value={values.contact_phone ?? values.member_user?.phone_number}
                                        onChange={(e) => setFieldValue('contact_phone', e.target.value)}
                                        placeholder={t('contact phone placeholder')}
                                    />
                                </Field>

                                {/* Mời tham gia Gia Phả qua contact_email - CHỈ hiện ở chế độ SỬA (cần
                                    id member thật để gọi resend-invite), CHƯA liên kết tài khoản (đã
                                    liên kết thì không cần mời nữa) và đã có contact_email. Tối đa 3
                                    lần/cách nhau 1 ngày - can_resend_invite do BE tính sẵn
                                    (FamilyBookMember::canResendInvite(), 1 nguồn duy nhất), FE chỉ đọc
                                    để disable nút + giải thích lý do, không tự tính lại. */}
                                {editingMember && !values.member_user_id && values.contact_email && (
                                    <div className="flex flex-col gap-1.5 p-2.5 rounded-lg bg-amber-50 border border-amber-200">
                                        <div className="flex items-center gap-1.5 text-xs text-amber-700">
                                            <IoAlertCircleOutline size={14} className="flex-shrink-0"/>
                                            {(editingMember.invite_sent_count || 0) > 0
                                                ? t('invite already sent info', {
                                                    count: editingMember.invite_sent_count,
                                                    date: editingMember.invite_last_sent_at ? dayjs(editingMember.invite_last_sent_at).format('DD/MM/YYYY HH:mm') : '',
                                                })
                                                : t('invite not sent yet info')}
                                        </div>
                                        <button
                                            type="button"
                                            disabled={!editingMember.can_resend_invite || resendingInvite}
                                            onClick={() => doResendInvite({}, {id: editingMember.id})}
                                            className="self-start px-3 py-1 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold cursor-pointer">
                                            {resendingInvite ? t('sending') : ((editingMember.invite_sent_count || 0) > 0 ? t('resend invite') : t('send invite'))}
                                        </button>
                                        {!editingMember.can_resend_invite && (editingMember.invite_sent_count || 0) > 0 && (
                                            <span className="text-2xs text-amber-600">
                                                {editingMember.invite_sent_count >= 3 ? t('invite limit reached') : t('invite wait before resend')}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                        </Section>

                        {/* native <input type="date"> qua antd Input (chỉ truyền type="date", không
                            phải antd DatePicker) - đã là pattern có sẵn cùng phân hệ Sổ Gia Đình
                            (EditFamilyBookModal.js, CreateFamilyBookModal trong FamilyBooks.js), giữ
                            nguyên UI/border/size đồng bộ với Select/ChipGroup phía trên (size="large")
                            NHƯNG trình duyệt vẫn render đúng bộ chọn ngày NATIVE của OS (bánh xe cuộn
                            trên iOS, native picker Android) - tiện hơn hẳn calendar popup của antd
                            DatePicker trên mobile, đúng theo phản hồi thực tế dùng thử trên iOS/Android. */}
                        <Section title={t('date of birth')} subtitle={t('optional')}>
                            <Input
                                type="date"
                                size="large"
                                className="w-full"
                                value={values.birth_date}
                                onChange={(e) => setFieldValue('birth_date', e.target.value)}
                            />
                        </Section>

                        {/* Ngày mất: để tách hẳn khỏi ngày sinh, mặc định thu gọn thành 1 dòng chữ
                            nhỏ, nhạt màu, không phải checkbox/nút nổi bật - tránh cảm giác "đập vào
                            mắt" một thông tin không may mắn ngay khi mở form thêm/sửa thân nhân. */}
                        {showDeathDate ? (
                            <Section title={`${t('date of death')} ✝`} subtitle={t('optional')} theme="neutral">
                                <Input
                                    type="date"
                                    size="large"
                                    className="w-full"
                                    value={values.death_date}
                                    onChange={(e) => setFieldValue('death_date', e.target.value)}
                                />
                                <span
                                    onClick={() => {
                                        setFieldValue('death_date', '')
                                        setShowDeathDate(false)
                                    }}
                                    className="w-fit text-2xs text-color-300 hover:text-color-500 italic cursor-pointer">
                                    {t('undo')}
                                </span>
                            </Section>
                        ) : (
                            <span
                                onClick={() => setShowDeathDate(true)}
                                className="w-fit text-xs text-color-300 hover:text-color-500 italic cursor-pointer">
                                {`+ ${t('add date of death')}`}
                            </span>
                        )}

                        <FormActions
                            onCancel={onClose}
                            submitText={isParentSplit ? 'create and link family book' : (editingMember ? 'save' : 'save relative')}
                            isSubmitting={isSubmitting || loading}
                            isValid={isValid}
                        />
                    </form>
                )
            }}
        </Formik>
    )
}

// Bọc DrawDialog (chuẩn của dự án) - visible=true cố định vì component chỉ mount khi cần (parent
// bọc `{showAdd && <AddRelativeModal .../>}`).
const AddRelativeModal = ({
                               familyBookId, book, editingMember,
                               anchorMemberId, anchorMemberName, defaultRelationshipType, lockRelationship, forcedForMember,
                               onClose, onSuccess, t,
                           }) => (
    <DrawDialog
        visible={true}
        width={480}
        wrapClassName="family-theme"
        component={<span style={{display: 'none'}}/>}
        onCancel={onClose}
        footer={false}>
        {(isToggle) => isToggle && (
            <AddRelativeForm
                familyBookId={familyBookId} book={book} editingMember={editingMember}
                anchorMemberId={anchorMemberId} anchorMemberName={anchorMemberName}
                defaultRelationshipType={defaultRelationshipType} lockRelationship={lockRelationship} forcedForMember={forcedForMember}
                onClose={onClose} onSuccess={onSuccess} t={t}/>
        )}
    </DrawDialog>
)

export default AddRelativeModal
